/**
 * Menu Icon Mapper Utility
 *
 * Maps icon name strings from backend to actual Ant Design icon components.
 * This allows the backend to specify icons without importing React components.
 */
import React from 'react';
import {
  DashboardOutlined,
  UserOutlined,
  SafetyOutlined,
  ShoppingOutlined,
  LineChartOutlined,
  FileTextOutlined,
  SettingOutlined,
  ToolOutlined,
  DatabaseOutlined,
  TeamOutlined,
  ShopOutlined,
  BookOutlined,
  ApartmentOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  SwapOutlined,
  TransactionOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  HomeOutlined,
  FolderOutlined,
  ProfileOutlined,
  BankOutlined,
  AccountBookOutlined,
  AuditOutlined,
  CalculatorOutlined,
  ClusterOutlined,
  DollarOutlined,
  FundOutlined,
  ReconciliationOutlined,
  ScheduleOutlined,
  SolutionOutlined,
  WalletOutlined,
} from '@ant-design/icons';

/**
 * Map of icon names to Ant Design icon component types
 */
const iconMap: Record<string, React.ComponentType> = {
  // Dashboard & Home
  DashboardOutlined,
  HomeOutlined,

  // User & Team
  UserOutlined,
  TeamOutlined,
  SafetyOutlined,
  SolutionOutlined,

  // Business & Commerce
  ShoppingOutlined,
  ShopOutlined,
  TransactionOutlined,
  BankOutlined,

  // Analytics & Charts
  LineChartOutlined,
  FundOutlined,

  // Documents & Files
  FileTextOutlined,
  FolderOutlined,
  ProfileOutlined,
  BookOutlined,
  AuditOutlined,

  // Settings & System
  SettingOutlined,
  ToolOutlined,
  DatabaseOutlined,
  ClusterOutlined,

  // Structure & Organization
  ApartmentOutlined,

  // Cloud & Sync
  CloudDownloadOutlined,
  CloudUploadOutlined,
  SwapOutlined,

  // Global & Network
  GlobalOutlined,

  // Finance & Accounting
  AccountBookOutlined,
  CalculatorOutlined,
  DollarOutlined,
  ReconciliationOutlined,
  WalletOutlined,
  ScheduleOutlined,

  // Default
  AppstoreOutlined,
};

/**
 * Get icon component from icon name string
 * @param iconName - Ant Design icon name (e.g., 'DashboardOutlined')
 * @returns React element for the icon, or default icon if not found
 */
export const getMenuIcon = (iconName?: string): React.ReactElement => {
  if (!iconName) {
    return React.createElement(AppstoreOutlined);
  }

  const IconComponent = iconMap[iconName] || AppstoreOutlined;
  return React.createElement(IconComponent);
};

/**
 * Check if an icon name is valid
 */
export const isValidIcon = (iconName: string): boolean => {
  return iconName in iconMap;
};
